<template>
  <section
    class="bg-gradient-to-r from-gray-100/70 to-gray-100 relative py-16 sm:py-24"
  >
    <div class="container relative max-w-4xl mx-auto px-4">
      <div v-if="loading" class="flex justify-center items-center h-screen">
        <i class="fas fa-spinner fa-spin text-5xl"></i>
      </div>

      <div v-else class="space-y-6">
        <!-- Ticket Details Card -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex justify-between items-start mb-4">
            <div>
              <h2 class="text-2xl font-medium mb-2">{{ ticket.Subject }}</h2>
              <p class="text-gray-600">{{ ticket.Description }}</p>
            </div>
            <div class="flex flex-col items-center gap-2">
              <span :class="getStatusBadgeClass(ticket.Status)">
                {{ getStatusText(ticket.Status) }}
              </span>
              <button
                v-if="ticket.Status !== 'CLOSED'"
                @click="handleCloseTicket"
                class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm"
                :disabled="isClosing"
                :class="{
                  'opacity-50 cursor-not-allowed': isClosing,
                }"
              >
                Talebi Kapat
                <i
                  :class="[
                    isClosing ? 'fas fa-spinner fa-spin' : 'fas fa-times',
                  ]"
                  aria-hidden="true"
                ></i>
              </button>
            </div>
          </div>
          <div class="text-sm text-gray-500">
            Oluşturulma: {{ formatDate(ticket.CreatedAt) }}
          </div>
        </div>

        <!-- Messages Section -->
        <div class="space-y-4 bg-white rounded-lg shadow-sm p-6">
          <div class="overflow-y-auto max-h-[600px] space-y-4">
            <div
              v-for="message in messages"
              :key="message.Id"
              class="flex"
              :class="[
                message.SenderType === 'USER' ? 'justify-start' : 'justify-end',
              ]"
            >
              <div
                class="flex flex-col max-w-[70%]"
                :class="[
                  message.SenderType === 'USER' ? 'items-start' : 'items-end',
                ]"
              >
                <span class="text-xs text-gray-500 mb-1">{{
                  message.SenderName
                }}</span>
                <div
                  class="rounded-2xl px-4 py-2 break-words"
                  :class="[
                    message.SenderType === 'USER'
                      ? 'bg-gray-100 rounded-tl-none'
                      : 'bg-blue-500 text-white rounded-tr-none',
                  ]"
                >
                  <p>{{ message.Message }}</p>
                  <span
                    class="text-xs mt-1 block"
                    :class="[
                      message.SenderType === 'USER'
                        ? 'text-gray-500'
                        : 'text-blue-100',
                    ]"
                  >
                    {{ formatDate(message.CreatedAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Message Input -->
          <div v-if="ticket.Status !== 'CLOSED'" class="mt-4 border-t pt-4">
            <div class="flex gap-2">
              <textarea
                v-model="newMessage"
                placeholder="Mesajınızı yazın..."
                class="flex-1 p-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 min-h-[50px] max-h-[150px] resize-y w-full"
                @keydown.enter.exact.prevent="handleSendMessage"
              ></textarea>
              <button
                @click="handleSendMessage"
                class="bg-blue-500 hover:bg-blue-600 text-white px-6 rounded-lg flex items-center"
                :disabled="isSending"
                :class="{
                  'opacity-50 cursor-not-allowed bg-gray-500': isSending,
                }"
              >
                <i
                  :class="[
                    isSending ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane',
                  ]"
                  aria-hidden="true"
                ></i>
              </button>
            </div>
            <span class="text-xs text-gray-500 mt-2">
              Mesaj göndermek için Enter tuşunu kullanabilirsiniz
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import {
  getTicketDetails,
  getTicketMessages,
  sendMessage,
  closeTicket,
} from "@/services/ticketServices";

const route = useRoute();
const ticket = ref({});
const messages = ref([]);
const loading = ref(true);
const newMessage = ref("");
const isClosing = ref(false);
const isSending = ref(false);

const ticketId = route.params.id;

const getStatusText = (status) => {
  const statusMap = {
    PENDING_ANSWER: "Yanıt Bekliyor",
    ANSWERED: "Yanıtlandı",
    CLOSED: "Kapatıldı",
  };
  return statusMap[status] || status;
};

const getStatusBadgeClass = (status) => {
  const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
  const statusClasses = {
    PENDING_ANSWER: "bg-yellow-100 text-yellow-800",
    ANSWERED: "bg-green-100 text-green-800",
    CLOSED: "bg-gray-100 text-gray-800",
  };
  return `${baseClasses} ${statusClasses[status]}`;
};

const fetchTicketData = async () => {
  try {
    const [ticketResponse, messagesResponse] = await Promise.all([
      getTicketDetails(ticketId),
      getTicketMessages(ticketId),
    ]);

    if (ticketResponse.Success) {
      ticket.value = ticketResponse.Payload;
    }

    if (messagesResponse.Success) {
      messages.value = messagesResponse.Payload;
    }
  } catch (error) {
    console.error("Data fetch error:", error);
  } finally {
    loading.value = false;
  }
};

const handleSendMessage = async () => {
  if (!newMessage.value.trim()) return;

  try {
    isSending.value = true;
    const response = await sendMessage(ticketId, newMessage.value);
    if (response.Success) {
      await fetchTicketData();
      newMessage.value = "";
    }
    isSending.value = false;
  } catch (error) {
    console.error("Message send error:", error);
    isSending.value = false;
  }
};

const handleCloseTicket = async () => {
  try {
    isClosing.value = true;
    const response = await closeTicket(ticketId);
    if (response.Success) {
      await fetchTicketData();
    }
    isClosing.value = false;
  } catch (error) {
    console.error("Close ticket error:", error);
    isClosing.value = false;
  }
};

const formatDate = (date) => {
  return new Date(date).toLocaleString("tr-TR");
};

onMounted(() => {
  fetchTicketData();
});
</script>
